import { useEffect, useMemo, useRef, useState } from "react";
import { publicSend } from "@/lib/derivPublic";
import { lastDigitsOf } from "@/lib/evenOdd";
import { riseFallSignal, diceSignal, luckySignal, evenOddSignal } from "./signalEngine";
import { setLiveMarket } from "@/lib/liveMarket";

const MARKETS = [
  ["R_10", "Vol 10"], ["R_25", "Vol 25"], ["R_50", "Vol 50"],
  ["R_75", "Vol 75"], ["R_100", "Vol 100"],
  ["1HZ10V", "V10 1s"], ["1HZ25V", "V25 1s"], ["1HZ50V", "V50 1s"],
  ["1HZ75V", "V75 1s"], ["1HZ100V", "V100 1s"],
];

/* ⚡ Signal Scanner — scans all 10 Volatility markets every 12s and runs the
   VTS Signal Engine per game type (momentum factors for Rise/Fall, hit-rate
   gaps for Over/Under, digit concentration for Match). Auto-locks the market
   with the strongest setup; says WAIT when conditions are mixed everywhere.
   Setups are descriptive conditions, never guarantees. */
const MarketScanner = ({ id, mode, bar = 4, onChange, note }) => {
  const [rows, setRows] = useState([]);
  const [pin, setPin] = useState(null);
  const cb = useRef(onChange);
  cb.current = onChange;

  useEffect(() => {
    let stop = false;
    const scan = async () => {
      const out = await Promise.all(MARKETS.map(async ([sym, label]) => {
        try {
          const h = await publicSend({ ticks_history: sym, count: 50, end: "latest", style: "ticks" });
          const raw = h?.history?.prices || [];
          const prices = raw.map(Number);
          if (prices.length < 20) return null;
          const freq = Array(10).fill(0);
          lastDigitsOf(raw).forEach((d) => { freq[d] += 1; });
          return { sym, label, n: prices.length, prices, freq };
        } catch { return null; }
      }));
      if (!stop) setRows(out.filter(Boolean));
    };
    scan();
    const t = setInterval(scan, 12000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const ranked = useMemo(() => rows.map((r) => {
    if (mode === "rise") {
      const signal = riseFallSignal(r.prices);
      const risePct = signal.risePct ?? 50;
      return { ...r, signal, risePct, heat: signal.wait ? signal.strength - 40 : signal.strength, line: `↑${risePct}% ↓${100 - risePct}%` };
    }
    if (mode === "evenodd") {
      const signal = evenOddSignal(r.prices);
      const e = signal.evenPct ?? 50;
      return { ...r, signal, evenPct: e, heat: signal.wait ? signal.strength - 40 : signal.strength, line: `E ${e}% / O ${100 - e}%` };
    }
    if (mode === "hilo") {
      const base = diceSignal(r.freq, r.n);
      const over = r.freq.reduce((s, c, d) => (d > bar ? s + c : s), 0);
      const under = r.freq.reduce((s, c, d) => (d < bar ? s + c : s), 0);
      const o = Math.round((over / r.n) * 100), u = Math.round((under / r.n) * 100);
      // banner reflects the GAME's actual bar, not the engine's own pick
      const typical = { OVER: (9 - bar) * 10, UNDER: bar * 10 };
      const side = o >= u ? "OVER" : "UNDER";
      const pct = Math.max(o, u);
      const signal = { ...base, side, bar, strength: pct, reasons: [`${side} ${bar} landed ${pct}% of the last ${r.n} ticks (typical ${typical[side]}%)`] };
      return { ...r, signal, overPct: o, underPct: u, heat: base.wait ? base.strength - 40 : pct, line: `O ${o}% / U ${u}%` };
    }
    const signal = luckySignal(r.freq, r.n);
    return { ...r, signal, topDigit: signal.digit, topPct: signal.topPct, heat: signal.wait ? signal.strength - 40 : signal.strength, line: `hot #${signal.digit} · ${signal.topPct}%` };
  }).sort((a, b) => b.heat - a.heat), [rows, mode, bar]);

  const selected = useMemo(
    () => (pin ? ranked.find((r) => r.sym === pin) || ranked[0] : ranked[0]),
    [ranked, pin],
  );

  useEffect(() => {
    if (!selected) return;
    cb.current?.(selected);
    setLiveMarket(selected.sym, selected.label); // 📡 tape + jumbotron follow the LOCKED pair
  }, [selected]);

  if (!ranked.length) {
    return (
      <div data-testid={`${id}-scanner`} className="rounded-lg border border-white/10 bg-[#0B0E14] p-2.5">
        <p className="text-[10px] font-mono text-white/40 animate-pulse">⚡ signal engine scanning 10 markets…</p>
      </div>
    );
  }

  const sig = selected?.signal;
  const sigLabel = sig && !sig.wait
    ? (mode === "rise" || mode === "evenodd" ? sig.side : mode === "hilo" ? `${sig.side} ${sig.bar}` : `digit ${sig.digit}`)
    : null;

  return (
    <div data-testid={`${id}-scanner`} className="rounded-lg border border-white/10 bg-[#0B0E14] p-2.5 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black font-mono text-[#F7B731]">⚡ SIGNAL ENGINE</span>
        <span className="text-[9px] font-mono text-white/35">10 markets · last 50 ticks · auto-locks the strongest setup · tap to pin</span>
        <button data-testid={`${id}-scanner-auto`} onClick={() => setPin(null)}
          className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-mono font-black border transition-colors ${pin === null
            ? "border-[#089981]/60 bg-[#089981]/10 text-[#089981]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
          AUTO {pin === null ? "ON" : "OFF"}
        </button>
      </div>

      {/* the setup card — the strategy layer */}
      {sig && (sigLabel ? (
        <div data-testid={`${id}-signal`} className="rounded-md border border-[#089981]/50 bg-[#089981]/10 px-2.5 py-2 space-y-1">
          <p className="text-[11px] font-black font-mono text-[#089981]">
            ⚡ SETUP FOUND — {sigLabel} on {selected.label} · strength {sig.strength}/100
          </p>
          <p className="text-[9px] font-mono text-white/60">{(sig.reasons || []).join(" · ")}</p>
          <p className="text-[8.5px] font-mono text-white/35">a setup = recent conditions lining up — it is NOT a guarantee, the next tick can still go either way</p>
        </div>
      ) : (
        <div data-testid={`${id}-signal-wait`} className="rounded-md border border-dashed border-[#F7B731]/40 bg-[#F7B731]/5 px-2.5 py-2">
          <p className="text-[11px] font-black font-mono text-[#F7B731]">⏳ NO CLEAR SETUP — conditions are mixed on all 10 markets</p>
          <p className="text-[9px] font-mono text-white/50">waiting IS the strategy: strong players sit out mixed conditions instead of guessing. The engine re-scans every 12s.</p>
        </div>
      ))}

      <div className="flex items-center gap-1.5 flex-wrap">
        {ranked.map((m) => {
          const isSel = selected?.sym === m.sym;
          return (
            <button key={m.sym} data-testid={`${id}-scan-${m.sym}`} onClick={() => setPin(m.sym)}
              className={`px-2 py-1 rounded-md text-[9px] font-mono border transition-colors ${isSel
                ? "border-[#F7B731]/60 bg-[#F7B731]/10 text-[#F7B731] font-black"
                : "border-white/10 text-white/50 hover:border-white/30"}`}>
              {isSel ? (pin ? "📌 " : "🎯 ") : ""}{m.label} · {m.line}{m.signal && !m.signal.wait ? ` · ⚡${m.signal.strength}` : ""}{isSel ? " · LOCKED" : ""}
            </button>
          );
        })}
      </div>
      <p className="text-[9px] font-mono text-white/35">{note || "stats are recent history only — they never predict the next tick"}</p>
    </div>
  );
};

export default MarketScanner;
